import { Tooltip } from '@shared/components/Tooltip';

import { CERTIFICATE_LABELS } from '../constants/labels';

/** Props for {@link ExportButton}. */
export interface ExportButtonProps {
  /** Click handler — expected to trigger an export flow. */
  onClick: () => void;
  /**
   * Disables the button. The enable/disable rule (export in progress, empty
   * list, missing privilege, …) is owned by the caller, not this component.
   */
  disabled: boolean;
  /**
   * Hover tooltip shown only while `disabled` is true — e.g. the shared
   * no-permission message. Omit for disabled states that need no explanation
   * (empty list, export in progress).
   */
  disabledTooltip?: string | undefined;
  /** Button text; defaults to the shared "Eksportuj" label. */
  label?: string;
  /**
   * Tailwind classes for the button surface (border/background/text/hover).
   * Defaults to the outlined secondary look used on the technician
   * "Moje certyfikaty" page; pass filled/primary classes to restyle
   * (e.g. the client Certyfikaty page per Figma).
   */
  colorClassName?: string;
  /** Stroke colour of the download icon; match it to `colorClassName`. */
  iconColor?: string;
}

const DEFAULT_COLOR_CLASSES =
  'border border-[#DCDDE0] text-[#474A4C] hover:bg-[#F6F8F9] hover:disabled:bg-transparent';
const DEFAULT_ICON_COLOR = '#474A4C';

/**
 * Reusable export button — the download icon never changes; label, colours,
 * disabled state and visibility (render it conditionally) are all decided by
 * the caller. Used by the technician (outlined default) and client (filled)
 * certificate list views.
 */
export function ExportButton({
  onClick,
  disabled,
  disabledTooltip,
  label = CERTIFICATE_LABELS.CERTS_TAB_EXPORT_BUTTON,
  colorClassName = DEFAULT_COLOR_CLASSES,
  iconColor = DEFAULT_ICON_COLOR,
}: ExportButtonProps) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-10 px-4 flex items-center gap-2 rounded-lg text-sm font-medium leading-5 tracking-[0.1px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none ${colorClassName}`}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.8335 8.33203L10.0002 12.4987L14.1668 8.33203"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 12.5V2.5"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {label}
    </button>
  );

  // Same disabled-with-explanation pattern as TabToggle — the tooltip only
  // wraps the button while it is disabled and a message is provided.
  if (disabled && disabledTooltip) {
    return (
      <Tooltip
        content={disabledTooltip}
        side="bottom"
        cursor="not-allowed"
        triggerTabIndex={-1}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
}
